import { useState } from 'react';

import { handleRenderError, withErrorHandler } from '@utils/errorHandler';

interface ITalentImageRowProperties {
  talentDetails: ITalent[];
}

export const TalentImageRow = ({
  talentDetails,
}: ITalentImageRowProperties) => {
  try {
    const [activeIndex, setActiveIndex] = useState(
      talentDetails.length === 1 ? 0 : -1
    );

    const handleToggleTalent = (index: number) => {
      if (talentDetails.length === 1) {
        return;
      }
      if (index === activeIndex) {
        return setActiveIndex(-1);
      }
      setActiveIndex(index);
    };

    return (
      <>
        {talentDetails.map((talent, index) => (
          <div
            className='featuringObject'
            key={talent.Name}
            onClick={withErrorHandler(() => handleToggleTalent(index))}
          >
            <div className='featuringObjectImage'>
              <img src={talent.Photo} alt={`${talent.Name}`} />
            </div>
            <div className={`info`}>
              <p className='name'>{talent.Name}</p>
              <p className='subtitle'>{talent.Subtitle}</p>
            </div>
            <div className='available-platform-icons-container'>
              {talent.AppleMusicURL ? (
                <ProviderIcon
                  provider='AppleMusic'
                  url={talent.AppleMusicURL}
                />
              ) : (
                <></>
              )}
              {talent.SpotifyURL ? (
                <ProviderIcon provider='Spotify' url={talent.SpotifyURL} />
              ) : (
                <></>
              )}
              {talent.SoundCloudURL ? (
                <ProviderIcon
                  provider='SoundCloud'
                  url={talent.SoundCloudURL}
                />
              ) : (
                <></>
              )}
              {talent.MixCloudURL ? (
                <ProviderIcon provider='MixCloud' url={talent.MixCloudURL} />
              ) : (
                <></>
              )}
            </div>
          </div>
        ))}
      </>
    );
  } catch (error) {
    return handleRenderError(error);
  }
};

const ProviderIcon = (properties: { provider: string; url: string }) => {
  switch (properties.provider) {
    case 'AppleMusic': {
      return (
        <a
          href={properties.url}
          className='available-platform-icon'
          target='_blank'
          rel='noreferrer'
        >
          <svg
            id='iconAppleMusic'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 100 100'
          >
            <defs></defs>
            <path
              className='cls-1'
              d='m100,27.69658c0-1.00008-.01389-2.01405-.04166-3.01412-.05556-2.19461-.19444-4.40312-.58333-6.56995-.38889-2.19461-1.04167-4.25033-2.05556-6.25048-1-1.95848-2.30556-3.76418-3.86111-5.31985-1.55556-1.55568-3.36111-2.86133-5.31945-3.86141-2-1.01397-4.04167-1.6668-6.23611-2.05571-2.16667-.38892-4.375-.51393-6.56944-.58338-1.01389-.01389-2.02778-.02778-3.02778-.04167H27.69445c-1,0-2.01389.01389-3.01389.04167-2.19444.05556-4.40278.19446-6.56944.58338-2.19445.40281-4.25,1.04175-6.23611,2.0696-1.97222,1.00008-3.76389,2.30573-5.31945,3.86141s-2.875,3.34748-3.875,5.31986c-1.01389,2.00015-1.66667,4.04198-2.05556,6.25048C.23611,20.29323.11111,22.50174.04167,24.69635c-.01389,1.00008-.02778,2.00015-.04167,3.00023v44.60067c0,1.00008.01389,2.01405.04167,3.01412.05556,2.19462.19444,4.40312.58333,6.56995.38889,2.19462,1.04167,4.25033,2.05556,6.25048,1,1.95848,2.30556,3.76418,3.86111,5.31985s3.36111,2.86133,5.31944,3.86141c2,1.01397,4.04167,1.66679,6.23611,2.05572,2.16667.38892,4.375.51393,6.56945.58338,1,.02778,2.01389.04167,3.01389.04167,1.19445.01389,2.38889,0,3.58333,0h41.01389c1,0,2.01389-.01389,3.01389-.04167,2.19445-.05556,4.40278-.19446,6.56944-.58338,2.19445-.38892,4.25-1.04175,6.23611-2.05572,1.95834-1.00008,3.76389-2.30574,5.31945-3.86141,1.55556-1.55568,2.86111-3.34749,3.86111-5.31986,1.01389-2.00015,1.66666-4.04198,2.05556-6.25048.38889-2.16684.51389-4.37534.58333-6.56995.02777-1.00008.04166-2.01405.04166-3.01412.01389-1.19454,0-2.38907,0-3.58361V31.28019c.04166-1.19454.04166-2.38907.04166-3.58361Zm-26.79167,37.37788c0,1.26399-.01389,2.41686-.27778,3.68084-.26389,1.23621-.73611,2.38907-1.45833,3.43082s-1.65278,1.88904-2.73611,2.52797c-1.09722.65283-2.25,1.01397-3.47222,1.26399-2.30556.45837-3.875.56949-5.36111.2778-1.43055-.29169-2.63889-.94452-3.61111-1.83347-1.43055-1.31955-2.33333-3.09746-2.52778-4.94483-.22222-2.18072.5-4.50035,2.125-6.2227.81944-.86118,1.86111-1.54179,3.25-2.08349,1.44445-.5556,3.04166-.90285,5.5-1.389.65278-.12501,1.29167-.26391,1.94445-.38892.84722-.16668,1.58333-.38892,2.16667-1.1112.59722-.72228.59722-1.61124.59722-2.47241v-22.02948c0-1.68068-.75-2.13906-2.36111-1.83347-1.15278.22224-25.86111,5.20874-25.86111,5.20874-1.38889.33336-1.88889.79173-1.88889,2.52797v32.26638c0,1.26399-.06944,2.41686-.33333,3.68084-.26389,1.23621-.73611,2.38907-1.45833,3.43082-.72222,1.04175-1.65278,1.88904-2.73611,2.52797-1.09722.65283-2.25,1.02786-3.47222,1.27787-2.30556.45837-3.875.56949-5.36111.2778-1.43056-.29169-2.63889-.95841-3.61111-1.84737-1.43056-1.31955-2.26389-3.09746-2.47222-4.94483-.22222-2.18072.43056-4.50035,2.06944-6.2227.81944-.86118,1.86111-1.54179,3.25-2.08349,1.44445-.5556,3.04167-.90285,5.5-1.389.65278-.12501,1.29167-.26391,1.94444-.38892.84722-.16668,1.58333-.38892,2.16667-1.1112s.65278-1.56957.65278-2.43074V25.52975c0-.50004.04167-.8334.06944-1.00008.125-.77784.43055-1.45844,1-1.9307.47222-.38892,1.08333-.66672,1.84722-.8334h.01389l29.75001-6.02824c.26389-.05556,2.40278-.43059,2.65278-.45837,1.61111-.1389,2.51389.91674,2.51389,2.6252l-.01389,47.1703h0Z'
            />
          </svg>
        </a>
      );
    }
    case 'Spotify': {
      return (
        <a
          href={properties.url}
          className='available-platform-icon'
          target='_blank'
          rel='noreferrer'
        >
          <svg
            id='iconSpotify'
            data-name='Layer 1'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 100 100'
          >
            <defs></defs>
            <path
              className='cls-1'
              d='M50,0a50,50,0,1,0,50,50A50,50,0,0,0,50,0ZM72.93047,72.11769a3.11416,3.11416,0,0,1-4.28487,1.03693c-11.74143-7.17632-26.51985-8.79687-43.92423-4.82127a3.11547,3.11547,0,1,1-1.3875-6.07449C42.38,57.90471,58.7177,59.7784,71.89671,67.83185A3.11677,3.11677,0,0,1,72.93047,72.11769Zm6.11825-13.61512a3.90033,3.90033,0,0,1-5.36209,1.28373c-13.43669-8.25947-33.92625-10.65213-49.823-5.82673a3.89763,3.89763,0,1,1-2.26559-7.45877c18.15984-5.51056,40.735-2.84159,56.16811,6.6426A3.89841,3.89841,0,0,1,79.04872,58.50257Zm.52778-14.17663c-16.11718-9.5715-42.702-10.45155-58.08782-5.78172a4.67638,4.67638,0,1,1-2.715-8.95c17.66181-5.36133,47.022-4.32565,65.57639,6.68837a4.67662,4.67662,0,0,1-4.77359,8.04338Z'
            />
          </svg>
        </a>
      );
    }
    case 'SoundCloud': {
      return (
        <a
          href={properties.url}
          className='available-platform-icon'
          target='_blank'
          rel='noreferrer'
        >
          <svg
            id='iconSoundcloud'
            data-name='Layer 1'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 230.26531 100'
          >
            <defs></defs>
            <path
              className='cls-1'
              d='M2.60678,59.8099a.95279.95279,0,0,0-.924.90176L0,73.49907,1.68282,86.07014a.94974.94974,0,0,0,.924.89846.9619.9619,0,0,0,.9185-.894h0v0L5.52053,73.49907,3.52528,60.70722A.9648.9648,0,0,0,2.60678,59.8099Zm9.46947-6.98827a.95774.95774,0,0,0-1.91464,0c0,.00444-2.26384,20.67744-2.26384,20.67744L10.16161,93.7222a.95656.95656,0,0,0,1.91247.00444l2.57627-20.22757ZM47.747,33.74493a1.77619,1.77619,0,0,0-1.74123,1.72677L44.20451,73.5191l1.80123,24.58846a1.74249,1.74249,0,0,0,3.48474-.00671v.00671L51.52854,73.5191,49.49048,35.4717A1.78877,1.78877,0,0,0,47.747,33.74493ZM29.28511,46.96864a1.38252,1.38252,0,0,0-1.35093,1.32986L25.90272,73.50795l2.03146,24.38829a1.35059,1.35059,0,0,0,2.70071,0l2.30723-24.38829L30.63489,48.29406A1.39007,1.39007,0,0,0,29.28511,46.96864Zm37.223,52.8534a2.16565,2.16565,0,0,0,2.13587-2.11924l1.768-24.17481-1.768-50.65082a2.13656,2.13656,0,0,0-4.273.00228L62.80323,73.528l1.56771,24.18369A2.15316,2.15316,0,0,0,66.50806,99.822Zm38.40822.11568a2.95725,2.95725,0,0,0,2.92091-2.911v0l1.23309-23.481-1.23309-58.50133a2.921,2.921,0,0,0-5.84194.00284l-1.10293,58.47845c0,.0378,1.10293,23.5166,1.10293,23.5166A2.94686,2.94686,0,0,0,104.91628,99.93772Zm-19.35144-.09336A2.54509,2.54509,0,0,0,88.09215,97.327v.01776l1.50213-23.80784L88.09215,22.51419a2.52963,2.52963,0,0,0-5.05918,0L81.69765,73.53687l1.3376,23.80124A2.53242,2.53242,0,0,0,85.56484,99.84436Zm-47.08546-.22248a1.57958,1.57958,0,0,0,1.54552-1.521l2.17377-24.5929L40.0249,50.1142a1.54631,1.54631,0,0,0-3.09217.01L35.017,73.50795l1.91578,24.59073A1.56653,1.56653,0,0,0,38.47938,99.62188ZM20.16416,98.27425a1.191,1.191,0,0,0,1.153-1.12742L23.759,73.50351,21.31948,48.9656A1.19489,1.19489,0,0,0,20.16416,47.837,1.18135,1.18135,0,0,0,19.0101,48.9656L16.86079,73.50351,19.0101,97.14023A1.18061,1.18061,0,0,0,20.16416,98.27425ZM95.205,21.66584A2.73093,2.73093,0,0,0,92.47523,24.381L91.25774,73.539l1.21749,23.65232a2.72591,2.72591,0,0,0,5.45164-.0156v.02l1.36652-23.65448L97.92687,24.37657A2.72974,2.72974,0,0,0,95.205,21.66584ZM57.09142,99.871a1.97528,1.97528,0,0,0,1.93912-1.92136l1.90132-24.4261-1.90132-46.748a1.94091,1.94091,0,0,0-3.88165,0l-1.68339,46.748,1.68669,24.435A1.96234,1.96234,0,0,0,57.09142,99.871Zm21.2383-2.30381V97.556l1.63341-24.02361L78.32972,21.17488a2.33282,2.33282,0,0,0-4.66556,0l-1.451,52.35755,1.45328,24.03249a2.33178,2.33178,0,0,0,4.66329-.00672ZM201.9431,43.35057a28.299,28.299,0,0,0-10.95441,2.19815A49.97906,49.97906,0,0,0,141.22017,0,50.6197,50.6197,0,0,0,123.103,3.38569c-2.13928.82729-2.70629,1.67838-2.72633,3.33012V96.60646a3.422,3.422,0,0,0,3.05766,3.34686c.07344.00888,78.004.04668,78.50882.04668a28.32472,28.32472,0,0,0,0-56.64943ZM114.58636,6.63577a3.15186,3.15186,0,0,0-3.12222,3.11276l-1.27635,63.80166,1.27863,23.15635a3.11866,3.11866,0,0,0,6.23715-.02448v.02664l1.3877-23.15851-1.3877-63.80667A3.14938,3.14938,0,0,0,114.58636,6.63577Z'
            />
          </svg>
        </a>
      );
    }
    case 'MixCloud': {
      return (
        <a
          href={properties.url}
          className='available-platform-icon'
          target='_blank'
          rel='noreferrer'
        >
          <svg
            id='iconMixcloud'
            data-name='Layer 1'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 354.88367 102.46461'
          >
            <path
              className='cls-1'
              d='M36.15231,0,56.29538,95.30462h4.68153L81.11691,0h36.15232V99.96461H98.99076V16.57692L102.39384,4.66H96.49538L75.50461,99.96461H41.90768L20.77229,4.66H15.02l3.25847,11.91692V99.96461H0V0ZM352.3837.49615V21.48077L312.116,49.98384l40.2677,28.49848V99.47L304.88214,65.29h-5.10461l-47.36,34.18V78.48077l40.12922-28.49693-40.12921-28.5V.49615l47.36,34.32h5.10461ZM236.84415,40.884V59.08246H136.61337V40.884Z'
            />
          </svg>
        </a>
      );
    }
    default: {
      return <></>;
    }
  }
};
